import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../Context/AuthContext';
import { PlatformIntegrationService } from '../services/platformIntegrationService';
import { UGCAnalyticsService } from '../services/ugcAnalyticsService';

export const useUGCAnalytics = () => {
  const { user } = useAuth();
  const [platformService, setPlatformService] = useState(null);
  const [ugcService, setUgcService] = useState(null);
  const [platformConnections, setPlatformConnections] = useState({
    metaAds: { connected: false, data: null },
    tiktok: { connected: false, data: null }
  });
  const [ugcData, setUgcData] = useState({
    metaAds: [],
    tiktok: []
  });
  const [analysis, setAnalysis] = useState({
    topPerformers: [],
    briefPerformance: [],
    scriptPerformance: [],
    creatorPerformance: [],
    recommendations: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Initialize services when user changes
  useEffect(() => {
    if (user?.uid) {
      setPlatformService(new PlatformIntegrationService(user.uid));
      setUgcService(new UGCAnalyticsService(user.uid));
    }
  }, [user]);

  // Connect to Meta Ads
  const connectMetaAds = useCallback(async (accessToken, adAccountId) => {
    if (!platformService) return;
    
    try {
      setLoading(true);
      const result = await platformService.connectMetaAds(accessToken, adAccountId);
      
      setPlatformConnections(prev => ({
        ...prev,
        metaAds: { connected: true, data: result }
      }));
      
      return result;
    } catch (err) {
      setError('Failed to connect Meta Ads');
      console.error('Error connecting Meta Ads:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [platformService]);

  // Connect to TikTok
  const connectTikTok = useCallback(async (accessToken, advertiserId) => {
    if (!platformService) return;
    
    try {
      setLoading(true);
      const result = await platformService.connectTikTok(accessToken, advertiserId);
      
      setPlatformConnections(prev => ({
        ...prev,
        tiktok: { connected: true, data: result }
      }));
      
      return result;
    } catch (err) {
      setError('Failed to connect TikTok');
      console.error('Error connecting TikTok:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [platformService]);

  // Fetch UGC data from Meta Ads
  const fetchMetaAdsUGC = useCallback(async (adAccountId, dateRange = 'last_30d') => {
    if (!platformService) return;
    
    try {
      setLoading(true);
      const data = await platformService.fetchMetaAdsUGCData(adAccountId, dateRange);
      
      setUgcData(prev => ({
        ...prev,
        metaAds: data
      }));
      
      return data;
    } catch (err) {
      setError('Failed to fetch Meta Ads UGC data');
      console.error('Error fetching Meta Ads UGC:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [platformService]);

  // Fetch UGC data from TikTok
  const fetchTikTokUGC = useCallback(async (advertiserId, dateRange = 'last_30d') => {
    if (!platformService) return;
    
    try {
      setLoading(true);
      const data = await platformService.fetchTikTokUGCData(advertiserId, dateRange);
      
      setUgcData(prev => ({
        ...prev,
        tiktok: data
      }));
      
      return data;
    } catch (err) {
      setError('Failed to fetch TikTok UGC data');
      console.error('Error fetching TikTok UGC:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [platformService]);

  // Analyze UGC performance
  const analyzeUGCPerformance = useCallback(async (platform) => {
    if (!ugcService) return;
    
    try {
      setLoading(true);
      const platformData = ugcData[platform] || [];
      const analysis = await ugcService.analyzeUGCPerformance(platform, platformData);
      
      setAnalysis(prev => ({
        ...prev,
        topPerformers: analysis.topPerformers,
        recommendations: analysis.recommendations
      }));
      
      return analysis;
    } catch (err) {
      setError('Failed to analyze UGC performance');
      console.error('Error analyzing UGC performance:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [ugcService, ugcData]);

  // Analyze brief performance
  const analyzeBriefPerformance = useCallback(async (briefId) => {
    if (!ugcService) return;
    
    try {
      setLoading(true);
      const briefAnalysis = await ugcService.analyzeBriefPerformance(briefId);
      
      setAnalysis(prev => ({
        ...prev,
        briefPerformance: [...prev.briefPerformance, briefAnalysis]
      }));
      
      return briefAnalysis;
    } catch (err) {
      setError('Failed to analyze brief performance');
      console.error('Error analyzing brief performance:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [ugcService]);

  // Analyze script performance
  const analyzeScriptPerformance = useCallback(async (scriptId) => {
    if (!ugcService) return;
    
    try {
      setLoading(true);
      const scriptAnalysis = await ugcService.analyzeScriptPerformance(scriptId);
      
      setAnalysis(prev => ({
        ...prev,
        scriptPerformance: [...prev.scriptPerformance, scriptAnalysis]
      }));
      
      return scriptAnalysis;
    } catch (err) {
      setError('Failed to analyze script performance');
      console.error('Error analyzing script performance:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [ugcService]);

  // Analyze creator performance
  const analyzeCreatorPerformance = useCallback(async (creatorId) => {
    if (!ugcService) return;
    
    try {
      setLoading(true);
      const creatorAnalysis = await ugcService.analyzeCreatorPerformance(creatorId);
      
      setAnalysis(prev => ({
        ...prev,
        creatorPerformance: [...prev.creatorPerformance, creatorAnalysis]
      }));
      
      return creatorAnalysis;
    } catch (err) {
      setError('Failed to analyze creator performance');
      console.error('Error analyzing creator performance:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [ugcService]);

  // Get platform connection status
  const getConnectionStatus = useCallback(async () => {
    if (!platformService) return;
    
    try {
      const status = await platformService.getConnectionStatus();
      setPlatformConnections(status);
      return status;
    } catch (err) {
      console.error('Error getting connection status:', err);
    }
  }, [platformService]);

  // Disconnect platform
  const disconnectPlatform = useCallback(async (platform) => {
    if (!platformService) return;
    
    try {
      setLoading(true);
      await platformService.disconnectPlatform(platform);
      
      setPlatformConnections(prev => ({
        ...prev,
        [platform]: { connected: false, data: null }
      }));
      
      setUgcData(prev => ({
        ...prev,
        [platform]: []
      }));
    } catch (err) {
      setError(`Failed to disconnect ${platform}`);
      console.error(`Error disconnecting ${platform}:`, err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [platformService]);

  // Load connection status on mount
  useEffect(() => {
    if (platformService) {
      getConnectionStatus();
    }
  }, [platformService, getConnectionStatus]);

  // Get top performing UGC across all platforms
  const getTopPerformingUGC = useCallback(() => {
    const allUGC = [
      ...ugcData.metaAds.map(item => ({ ...item, platform: 'Meta Ads' })),
      ...ugcData.tiktok.map(item => ({ ...item, platform: 'TikTok' }))
    ];
    
    return allUGC
      .sort((a, b) => (b.views || 0) - (a.views || 0))
      .slice(0, 10);
  }, [ugcData]);

  // Get top performing creators across all platforms
  const getTopPerformingCreators = useCallback(() => {
    const allCreators = [
      ...ugcData.metaAds.map(item => ({ 
        ...item.creator, 
        platform: 'Meta Ads',
        performance: item.views || 0
      })),
      ...ugcData.tiktok.map(item => ({ 
        ...item.creator, 
        platform: 'TikTok',
        performance: item.views || 0
      }))
    ];
    
    // Group by creator and calculate total performance
    const creatorMap = {};
    allCreators.forEach(creator => {
      const key = creator.id || creator.handle;
      if (!creatorMap[key]) {
        creatorMap[key] = {
          ...creator,
          totalPerformance: 0,
          contentCount: 0,
          platforms: []
        };
      }
      creatorMap[key].totalPerformance += creator.performance;
      creatorMap[key].contentCount += 1;
      if (!creatorMap[key].platforms.includes(creator.platform)) {
        creatorMap[key].platforms.push(creator.platform);
      }
    });
    
    return Object.values(creatorMap)
      .sort((a, b) => b.totalPerformance - a.totalPerformance)
      .slice(0, 10);
  }, [ugcData]);

  return {
    // State
    platformConnections,
    ugcData,
    analysis,
    loading,
    error,
    
    // Platform connections
    connectMetaAds,
    connectTikTok,
    disconnectPlatform,
    getConnectionStatus,
    
    // Data fetching
    fetchMetaAdsUGC,
    fetchTikTokUGC,
    
    // Analysis
    analyzeUGCPerformance,
    analyzeBriefPerformance,
    analyzeScriptPerformance,
    analyzeCreatorPerformance,
    
    // Utilities
    getTopPerformingUGC,
    getTopPerformingCreators,
    clearError: () => setError(null)
  };
};


